import {
  Blend,
  CalendarRange,
  ChartColumn,
  CircleCheckBig,
  ListOrdered,
  LucideIcon,
  ReceiptText,
  Repeat2,
  Route,
  Settings2,
  SlidersHorizontal,
  Store,
  Tags,
  Target,
  Users,
  Wallet,
} from 'lucide-react';
import { TitleEntry } from '../../../navigation/titleEntry';
import {
  ARCHON_ACCURACY_PATH,
  ARCHON_AUTOMATCH_QA_PATH,
  ARCHON_DATA_COST_MODEL_PATH,
  ARCHON_DATA_INGRESS_SOURCES_PATH,
  ARCHON_DATA_INVOICES_PATH,
  ARCHON_DATA_MATCH_CONFIG_PATH,
  ARCHON_DATA_QA_SAMPLING_PATH,
  ARCHON_DATA_ROLLOUT_PATH,
  ARCHON_HOW_THE_ESTIMATE_WORKS_PATH,
  ARCHON_KIT_PATH,
  ARCHON_LABELERS_PATH,
  ARCHON_MATCH_TRACE_PATH,
  ARCHON_MOD_ORDER_PATH,
  ARCHON_OVERLAPS_PATH,
  ARCHON_PER_STORE_PATH,
  ARCHON_REPEAT_TASKS_PATH,
  ARCHON_TASKS_COSTS_PATH,
} from '../../paths';

/**
 * The one list of Archon destinations. The sidebar renders it, the mobile
 * topbar takes its label from it, and the browser tab is named from it.
 */

export interface ArchonNavItem {
  label: string;
  icon: LucideIcon;
  path: string;
  /** What the browser tab calls this destination, where the sidebar label is too long for it. */
  tabLabel?: string;
  /** Offered only to the owners of Archon's fleet-wide inputs; the route and api_dl refuse anyone else. */
  ownerOnly?: boolean;
}

export interface ArchonNavSection {
  title: string;
  items: ArchonNavItem[];
}

export const ARCHON_NAV_SECTIONS: ArchonNavSection[] = [
  {
    title: 'Dashboards',
    items: [
      { label: 'Tasks & Costs', icon: Wallet, path: ARCHON_TASKS_COSTS_PATH },
      { label: 'Accuracy', icon: Target, path: ARCHON_ACCURACY_PATH },
      { label: 'AutoMatch QA', icon: CircleCheckBig, path: ARCHON_AUTOMATCH_QA_PATH },
      { label: 'Labelers', icon: Users, path: ARCHON_LABELERS_PATH },
      { label: 'Per store', icon: Store, path: ARCHON_PER_STORE_PATH },
      { label: 'Repeat tasks', tabLabel: 'Repeats', icon: Repeat2, path: ARCHON_REPEAT_TASKS_PATH },
      { label: 'Overlaps', icon: Blend, path: ARCHON_OVERLAPS_PATH },
    ],
  },
  {
    title: 'Tools',
    items: [
      { label: 'Match Trace', tabLabel: 'Trace', icon: Route, path: ARCHON_MATCH_TRACE_PATH },
      { label: 'Mod order', icon: ListOrdered, path: ARCHON_MOD_ORDER_PATH },
    ],
  },
  {
    title: 'Data',
    items: [
      {
        label: 'Rollout schedules',
        tabLabel: 'Rollouts',
        icon: CalendarRange,
        path: ARCHON_DATA_ROLLOUT_PATH,
      },
      {
        label: 'Paid invoices',
        icon: ReceiptText,
        path: ARCHON_DATA_INVOICES_PATH,
        ownerOnly: true,
      },
      {
        label: 'Cost model',
        icon: SlidersHorizontal,
        path: ARCHON_DATA_COST_MODEL_PATH,
        ownerOnly: true,
      },
      {
        label: 'Origins',
        tabLabel: 'Origins',
        icon: Tags,
        path: ARCHON_DATA_INGRESS_SOURCES_PATH,
      },
      {
        label: 'QA sampling',
        icon: ChartColumn,
        path: ARCHON_DATA_QA_SAMPLING_PATH,
        ownerOnly: true,
      },
      {
        label: 'Match config',
        tabLabel: 'Config',
        icon: Settings2,
        path: ARCHON_DATA_MATCH_CONFIG_PATH,
        ownerOnly: true,
      },
    ],
  },
];

export const ARCHON_APP_TITLE = 'Archon';

/**
 * Real pages the sidebar does not offer: the explainer is reached from Tasks &
 * Costs (and keeps that item current), the kit showcase by URL only.
 */
const UNLISTED_PAGES: { path: string; label: string; tabLabel?: string }[] = [
  {
    path: ARCHON_HOW_THE_ESTIMATE_WORKS_PATH,
    label: 'How the estimate works',
    tabLabel: 'How it works',
  },
  { path: ARCHON_KIT_PATH, label: 'UI kit', tabLabel: 'Kit' },
];

/** Top bar title on mobile: the label of the page that owns the path, else 'Archon'. */
export function getArchonPageLabel(pathname: string): string {
  const pages = [...ARCHON_NAV_SECTIONS.flatMap((section) => section.items), ...UNLISTED_PAGES];
  return pages.find((page) => page.path === pathname)?.label ?? ARCHON_APP_TITLE;
}

/** Browser tab names for Archon. */
export const ARCHON_TITLE_ENTRIES: TitleEntry[] = [
  ...ARCHON_NAV_SECTIONS.flatMap((section) => section.items),
  ...UNLISTED_PAGES,
].map((page) => ({ path: page.path, label: page.tabLabel ?? page.label }));
